import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';

export enum FieldMapperType {
  FIELD_EXTRACTOR,
  FUNCTION_EXTRACTOR,
}

export interface FieldMapper<T> {
  readonly mapperType: FieldMapperType;
  readonly fieldChain?: string[];
  readonly extractFunction?: (input: T) => string | undefined;
}

export interface MultiFieldMapper<T> {
  readonly [index: string]: FieldMapper<T>;
}

@Injectable({
  providedIn: 'root',
})
export class CSVService {
  public convertToCSV<T>(
    multiFieldMapper: MultiFieldMapper<T>,
    objects: T[]
  ): string {
    const content = '\ufeff';
    const header = Object.keys(multiFieldMapper)
      .map(header => '"' + header + '"')
      .join(',');

    return this.readValues(
      multiFieldMapper,
      objects,
      content + header + '\r\n',
      true
    );
  }

  public convertToCSVWithoutQuotes<T>(
    multiFieldMapper: MultiFieldMapper<T>,
    objects: T[]
  ): string {
    const content = '\ufeff';
    const header = Object.keys(multiFieldMapper)
      .map(header => header)
      .join(',');

    return this.readValues(
      multiFieldMapper,
      objects,
      content + header + '\r\n',
      false
    );
  }

  private readValues<T>(
    multiFieldMapper: MultiFieldMapper<T>,
    objects: T[],
    content: string,
    quote: boolean
  ): string {
    for (let i = 0; i < objects.length; i++) {
      const line = new Array<string>();
      const currentGym = objects[i];
      Object.values(multiFieldMapper).forEach(fieldMapper => {
        const value = this.extractValue(fieldMapper, currentGym) || '';
        quote ? line.push(this.quoteValue(value)) : line.push(value);
      });
      content += line.join(',') + '\r\n';
    }
    return content;
  }

  public convertCSVFileToObject<T>(
    inputFile: File,
    fieldMap: string[],
    type: { new (): T }
  ): Observable<T[]> {
    const reader = new FileReader();
    reader.readAsText(inputFile);

    return new Observable((subscriber: Subscriber<T[]>) => {
      reader.onload = () => {
        const returnObject: T[] = [];
        const csv = reader.result as string;
        const lines = csv.split('\n');

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].replace('\r', '').split(',');
          const object = new type();
          fieldMap.forEach((field, index) => {
            object[field as keyof T] = line[index] as any;
          });
          returnObject.push(object);
        }
        subscriber.next(returnObject);
        subscriber.complete();
      };

      reader.onerror = () => {
        subscriber.error(['']);
      };
    });
  }

  private quoteValue(value: string): string {
    return value ? '"' + value.replace(/"/g, '""') + '"' : '""';
  }

  private extractValue<T>(
    fieldMapper: FieldMapper<T>,
    object: T
  ): string | undefined {
    switch (fieldMapper.mapperType) {
      case FieldMapperType.FIELD_EXTRACTOR:
        return this.extractFieldValue(object, fieldMapper.fieldChain);
      case FieldMapperType.FUNCTION_EXTRACTOR:
        return this.extractWithFunction(object, fieldMapper.extractFunction);
      default:
        break;
    }
    return undefined;
  }

  private extractFieldValue<T>(
    object: T,
    fieldStructure: string[] | undefined
  ): string | undefined {
    if (!object || !fieldStructure) {
      return undefined;
    }
    const val = object[fieldStructure[0] as keyof T];
    if (fieldStructure.length > 1) {
      return this.extractFieldValue(
        val,
        fieldStructure.slice(1, fieldStructure.length)
      );
    }
    return val !== undefined && val !== null ? '' + val : undefined;
  }

  private extractWithFunction<T>(
    input: T,
    fn?: (input: T) => string | undefined
  ): string | undefined {
    return fn ? fn(input) : undefined;
  }
}
